import React, { useState } from "react";
import { StyleSheet, View, Alert } from "react-native";
import { useAuth } from "@/hooks/auth";
import { colors, layout } from "@/constants";
import {
  ScreenLayout,
  TextButton,
  NotificationCard,
  Text,
  Title,
  Checkbox,
  ScrollView
} from "@/components";
import agreementsData from "@/assets/agreements/agreements.json";

/**
 * Consent screen component that displays testing agreement and data privacy consent
 * Requires user acceptance before accessing the main app functionality
 */
export default function ConsentScreen() {
  const { recordConsent, isLoading, error } = useAuth();
  const [hasConfirmedAge, setHasConfirmedAge] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [hasAcceptedRecording, setHasAcceptedRecording] = useState(false);

  const handleContinue = async () => {
    if (!hasConfirmedAge || !hasAccepted || !hasAcceptedRecording) {
      Alert.alert(
        "Consent Required",
        "Please accept all agreements to continue."
      );
      return;
    }

    const success = await recordConsent({
      version: agreementsData.version
    });

    if (!success) {
      Alert.alert("Error", "Failed to record consent. Please try again.");
    }
    // Note: No manual navigation needed — AuthGate in (drawer)/_layout.tsx
    // re-evaluates as soon as the user doc reflects the new consent and
    // redirects on to the next onboarding step (/settings) or /chat.
  };

  return (
    <ScreenLayout>
      <ScrollView>
        {/* Header */}
        <View style={styles.header}>
          <Title>Before You Begin</Title>
          <Text>{agreementsData.summary}</Text>
        </View>

        {/* Terms of Service Section */}
        <View style={styles.agreementSection}>
          <Text variant="semiBold" color="primary" style={styles.sectionTitle}>
            Terms of Service & Early Preview Agreement
          </Text>
          <View style={styles.agreementContent}>
            <Text size="small" color="secondary">
              {agreementsData.tos}
            </Text>
          </View>
        </View>

        {/* Privacy Policy Section */}
        <View style={styles.agreementSection}>
          <Text variant="semiBold" color="primary" style={styles.sectionTitle}>
            Privacy Policy
          </Text>
          <View style={styles.agreementContent}>
            <Text size="small" color="secondary">
              {agreementsData.privacyPolicy}
            </Text>
          </View>
        </View>

        {/* Consent Checkboxes */}
        <View style={styles.consentSection}>
          <Checkbox
            checked={hasConfirmedAge}
            onToggle={() => setHasConfirmedAge(!hasConfirmedAge)}
            label={agreementsData.confirmAge}
          />
          <View style={styles.checkboxSpacer} />
          {/* COMPLIANCE-EXCEPTION: refusal copy ("cannot provide ... diagnosis"); M5 may revisit phrasing with counsel */}
          <Checkbox
            checked={hasAccepted}
            onToggle={() => setHasAccepted(!hasAccepted)}
            label={agreementsData.confirmTerms}
          />
          <View style={styles.checkboxSpacer} />
          <Checkbox
            checked={hasAcceptedRecording}
            onToggle={() => setHasAcceptedRecording(!hasAcceptedRecording)}
            label={agreementsData.confirmRecording}
          />
        </View>

        {/* Error Display */}
        {error && (
          <View style={styles.errorContainer}>
            <NotificationCard
              variant="error"
              label="Consent Error"
              message={error}
            />
          </View>
        )}

        {/* Continue Button */}
        <View style={styles.buttonContainer}>
          <TextButton
            buttonText={isLoading ? "Recording..." : "Continue"}
            onPress={handleContinue}
            disabled={
              !hasConfirmedAge ||
              !hasAccepted ||
              !hasAcceptedRecording ||
              isLoading
            }
            width={undefined}
          />
        </View>
      </ScrollView>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  header: {
    alignItems: "center",
    marginBottom: layout.space.xl
  },
  agreementSection: {
    marginBottom: layout.space.xl
  },
  sectionTitle: {
    marginBottom: layout.space.md
  },
  agreementContent: {
    borderWidth: layout.borderWidth.thin,
    borderColor: colors.outline,
    borderRadius: layout.borderRadius.medium,
    backgroundColor: colors.surface,
    padding: layout.space.lg
  },
  consentSection: {
    marginBottom: layout.space.xl
  },
  checkboxSpacer: {
    height: layout.space.md
  },
  errorContainer: {
    marginBottom: layout.space.lg
  },
  buttonContainer: {
    alignItems: "center",
    paddingBottom: layout.space.xl
  }
});
